/**
 * Shapes the /customers/ response into CollapsibleGroupedSelect `groups`.
 *
 * Each customer carries `arkbridge_customer_id` (ArkBridge cid, used as group key)
 * and `linked_orders: [{order_number, tapeark_job_id}]`.
 *
 * Rules:
 * - group key = Number(arkbridge_customer_id); customers without one are skipped
 *   (no cid → can't scope SaaS calls).
 * - item key  = Number(tapeark_job_id); jobs without one are skipped
 *   (no ArkBridge job id → can't filter). 0 counts as "without one": it is the
 *   Django default for tapeark_job_id (tape/models.py), so an order that has
 *   never been linked to an ArkBridge job carries 0 rather than nothing. It has
 *   to be rejected explicitly because isNaN(0) is false — and selecting it sends
 *   job_id: 0 to the SaaS, which becomes `vi.JobID=0` and matches no media at
 *   all, so the job appears in the selector and then reports zero of everything.
 * - item label = order_number (human ref, e.g. "RIO-080"); falls back to the id.
 * - customers with no usable jobs are omitted.
 * - sorted: groups by company_name, items by order_number.
 *
 * @param {Array} customers — array of customer objects from fetchCustomers()
 * @returns {Array<{key:number,label:string,items:Array<{key:number,label:string}>}>}
 */
export const buildJobGroups = (customers) => {
    if (!Array.isArray(customers)) return [];

    const groups = [];
    customers.forEach((c) => {
        const cid = parseInt(c?.arkbridge_customer_id, 10);
        if (isNaN(cid)) return;

        const orders = Array.isArray(c?.linked_orders) ? c.linked_orders : [];
        const items = [];
        orders.forEach((o) => {
            const jobId = parseInt(o?.tapeark_job_id, 10);
            // > 0, not just !isNaN: 0 is the unset default, and isNaN(0) is false.
            if (!(jobId > 0)) return;
            items.push({key: jobId, label: o?.order_number || String(jobId)});
        });
        if (!items.length) return;

        items.sort((a, b) => String(a.label).localeCompare(String(b.label)));
        groups.push({key: cid, label: c?.company_name || String(cid), items});
    });

    groups.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return groups;
};

/**
 * Builds a single-group structure for a customer-role user, from their own jobs.
 * Used because customer-role cannot call /customers/ (IsSuperUser-only); instead we
 * fetch their orders via fetchJobsByCustomer and group under their own company.
 *
 * @param {Array} jobs            — order objects [{order_number, tapeark_job_id}]
 * @param {string|number} cid     — the user's arkbridge_customer_id (group key)
 * @param {string} companyName    — the user's company_name (group label)
 * @returns {Array} groups        — [] if no usable jobs or no numeric cid
 */
export const buildOwnJobGroup = (jobs, cid, companyName) => {
    const numericCid = parseInt(cid, 10);
    if (isNaN(numericCid) || !Array.isArray(jobs)) return [];

    const items = [];
    jobs.forEach((o) => {
        const jobId = parseInt(o?.tapeark_job_id, 10);
        // > 0, not just !isNaN: 0 is the unset default, and isNaN(0) is false.
        if (!(jobId > 0)) return;
        items.push({key: jobId, label: o?.order_number || String(jobId)});
    });
    if (!items.length) return [];

    items.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return [{key: numericCid, label: companyName || String(numericCid), items}];
};
